import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  ChartBarIcon, UsersIcon, TicketIcon, ArrowsRightLeftIcon,
  StarIcon, DocumentTextIcon, Cog6ToothIcon, ArrowRightOnRectangleIcon,
  FunnelIcon, GiftIcon, BellIcon, ClockIcon, ChatBubbleLeftRightIcon,
  MegaphoneIcon, BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';
import { MapPinIcon as PinSolid } from '@heroicons/react/24/solid';
import { MapPinIcon as PinOutline } from '@heroicons/react/24/outline';
import logoImg from '../assets/logo.png';
import iconImg from '../assets/icon.png';
import styles from './Layout.module.css';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard',  Icon: ChartBarIcon },
  { path: '/activity',  label: 'Activity',   Icon: ClockIcon },
  { path: '/members',   label: 'App Members', Icon: UsersIcon },
  { path: '/bc-members', label: 'All Customers', Icon: BuildingStorefrontIcon },
  { path: '/coupons',   label: 'Coupons',    Icon: TicketIcon },
  { path: '/referrals', label: 'Referrals',  Icon: ArrowsRightLeftIcon },
  { path: '/loyalty',   label: 'Loyalty',    Icon: StarIcon },
  { path: '/crm',            label: 'CRM Segments', Icon: FunnelIcon },
  { path: '/promo-codes',    label: 'Promo Codes',  Icon: GiftIcon },
  { path: '/notifications',  label: 'Notifications', Icon: BellIcon },
  { path: '/campaigns',      label: 'WhatsApp',     Icon: ChatBubbleLeftRightIcon },
  { path: '/promotions', label: 'Promotions', Icon: MegaphoneIcon },
  { path: '/pages',     label: 'App Pages',  Icon: DocumentTextIcon },
  { path: '/settings',  label: 'Settings',   Icon: Cog6ToothIcon },
];

const PAGE_TITLES = {
  '/dashboard': 'Dashboard',
  '/activity':  'Activity',
  '/members':   'App Members',
  '/bc-members': 'All Customers (Business Central)',
  '/coupons':   'Coupons',
  '/referrals': 'Referrals',
  '/loyalty':   'Loyalty',
  '/crm':            'CRM Segments',
  '/promo-codes':    'Promo Codes',
  '/notifications':  'Push Notifications',
  '/campaigns':      'WhatsApp Campaigns',
  '/pages':     'App Pages',
  '/settings':  'Settings',
};

function getAdminInfo() {
  try { return JSON.parse(localStorage.getItem('admin_info') || '{}'); } catch { return {}; }
}

function AdminAvatar({ name }) {
  const initials = name ? name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() : 'A';
  return <div className={styles.adminAvatar}>{initials}</div>;
}

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const admin    = getAdminInfo();

  // Sidebar state: pinned = always expanded, unpinned = collapsed, hover to expand
  const [pinned, setPinned] = useState(() => {
    const saved = localStorage.getItem('sidebar_pinned');
    return saved !== null ? saved === 'true' : true;
  });
  const [hovered, setHovered] = useState(false);

  const expanded = pinned || hovered;

  useEffect(() => {
    localStorage.setItem('sidebar_pinned', String(pinned));
  }, [pinned]);

  const pageTitle = Object.entries(PAGE_TITLES).find(([path]) =>
    location.pathname.startsWith(path)
  )?.[1] || 'Admin';

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_info');
    navigate('/login');
  };

  return (
    <div className={styles.shell}>
      {/* ── Sidebar ── */}
      <aside
        className={`${styles.sidebar} ${expanded ? styles.sidebarExpanded : styles.sidebarCollapsed}`}
        onMouseEnter={() => !pinned && setHovered(true)}
        onMouseLeave={() => !pinned && setHovered(false)}
      >
        {/* Logo area */}
        <div className={styles.logo}>
          <img src={iconImg} alt="TTS" className={styles.logoIcon} />
          <div className={styles.logoText}>
            <div className={styles.logoTitle}>The Toy Store</div>
            <div className={styles.logoSub}>VIP Admin</div>
          </div>
        </div>

        {/* Navigation */}
        <nav className={styles.nav}>
          <div className={`${styles.navSection} ${expanded ? styles.show : styles.hide}`}>MENU</div>
          {NAV_ITEMS.map(({ path, label, Icon }) => (
            <NavLink
              key={path}
              to={path}
              title={!expanded ? label : undefined}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className={styles.activeBar} />}
                  <span className={styles.navIconBox}>
                    <Icon style={{ width: 20, height: 20 }} />
                  </span>
                  <span className={`${styles.navLabel} ${expanded ? styles.show : styles.hide}`}>{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Pin toggle */}
        <button
          className={`${styles.pinBtn} ${expanded ? styles.show : styles.hide}`}
          onClick={() => setPinned(p => !p)}
          title={pinned ? 'Unpin sidebar' : 'Pin sidebar'}
        >
          {pinned
            ? <PinSolid style={{ width: 16, height: 16 }} />
            : <PinOutline style={{ width: 16, height: 16 }} />
          }
          <span>{pinned ? 'Unpin sidebar' : 'Pin sidebar'}</span>
        </button>

        {/* Admin footer */}
        <div className={styles.adminInfo}>
          <AdminAvatar name={admin.fullName} />
          <div className={`${styles.adminMeta} ${expanded ? styles.show : styles.hide}`}>
            <div className={styles.adminName}>{admin.fullName || 'Admin'}</div>
            <div className={styles.adminRole}>{admin.role || 'administrator'}</div>
          </div>
          <button
            className={`${styles.logoutIconBtn} ${expanded ? styles.show : styles.hide}`}
            onClick={handleLogout}
            title="Sign Out"
          >
            <ArrowRightOnRectangleIcon style={{ width: 17, height: 17 }} />
          </button>
        </div>
      </aside>

      {/* Spacer when sidebar is collapsed (floating mode) */}
      {!pinned && <div className={styles.sidebarSpacer} />}

      {/* ── Right pane ── */}
      <div className={styles.rightPane}>
        <header className={styles.topBar}>
          <div className={styles.topBarLeft}>
            <img src={logoImg} alt="The Toy Store" className={styles.topBarLogo} />
            <span className={styles.topBarDivider} />
            <span className={styles.topBarPage}>{pageTitle}</span>
          </div>
          <div className={styles.topBarRight}>
            <span className={styles.topBarDate}>
              {new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          </div>
        </header>
        <main className={styles.main}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
